import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box, FormControl, InputLabel, MenuItem, Paper,
  Select, Stack, Typography,
} from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import InboxList   from './InboxList';
import InboxReader from './InboxReader';

/**
 * Brand contact inbox.
 *
 * Two-column layout: list on the left, reader on the right. Owns
 * loading, status filter, selection, and the read/archive/delete
 * actions. Replies leave the app via `mailto:` so the brand owner's
 * normal email client picks up the thread.
 */
export default function Inbox() {
  usePageTitle('Contact inbox', 'Messages sent from the contact form on your site.');
  const [selectedBrand] = useOutletContext();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listBrandContact({
      brand_id: selectedBrand,
      status: statusFilter || undefined,
    })
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load inbox', { toastId: 'inbox-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand, statusFilter]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandContact({
      brand_id: selectedBrand,
      status: statusFilter || undefined,
    })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load inbox', { toastId: 'inbox-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand, statusFilter]);

  const selected = useMemo(
    () => items.find((m) => m._id === selectedId) || null,
    [items, selectedId]
  );

  // Open auto-marks `new` messages as read with an optimistic local
  // update so the list reflects the change immediately.
  const openMessage = (m) => {
    setSelectedId(m._id);
    if (m.status === 'new') {
      API.setBrandContactStatus({ brand_id: selectedBrand, _id: m._id, status: 'read' })
        .then(() => setItems((prev) => prev.map((x) => x._id === m._id ? { ...x, status: 'read' } : x)))
        .catch(() => { /* keep local optimistic state */ });
    }
  };

  const archive = (m) => {
    API.setBrandContactStatus({ brand_id: selectedBrand, _id: m._id, status: 'archived' })
      .then(() => { toast.success('Archived'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  const remove = (m) => {
    if (!window.confirm(`Delete the message from ${m.name}?`)) return;
    API.deleteBrandContact({ brand_id: selectedBrand, _id: m._id })
      .then(() => {
        toast.success('Deleted');
        if (selectedId === m._id) setSelectedId(null);
        load();
      })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          p: { xs: '20px', md: '28px' },
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <Stack
          direction='row' justifyContent='space-between' alignItems='center'
          flexWrap='wrap' gap='10px' mb='14px'
        >
          <Box>
            <Typography variant='h6' fontWeight='700'>Contact inbox</Typography>
            <Typography variant='body2' color='text.secondary'>
              Messages from your storefront /contact form. Reply via email from your usual client.
            </Typography>
          </Box>
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              displayEmpty
              label='Status'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value=''>All messages</MenuItem>
              <MenuItem value='new'>New</MenuItem>
              <MenuItem value='read'>Read</MenuItem>
              <MenuItem value='archived'>Archived</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {!selectedBrand && (
          <Typography variant='body2' color='text.secondary'>
            Select a brand to see its inbox.
          </Typography>
        )}

        {selectedBrand && (
          <Stack
            direction='row'
            sx={{
              border: '1px solid', borderColor: 'divider',
              borderRadius: '12px', overflow: 'hidden',
              minHeight: 400,
            }}
          >
            <InboxList
              items={items}
              loading={loading}
              selectedId={selectedId}
              onOpen={openMessage}
            />
            <InboxReader
              message={selected}
              onArchive={archive}
              onDelete={remove}
            />
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
